import React, { createContext, useContext, useState, useEffect } from 'react';

// ============================================================================
// LOCATION CONTEXT
// ============================================================================

const LocationContext = createContext();

export const LocationProvider = ({ children }) => {
  const [userLocation, setUserLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Load location from localStorage on mount
  useEffect(() => {
    const savedLocation = localStorage.getItem('userLocation');
    if (savedLocation) {
      try {
        const locationData = JSON.parse(savedLocation);
        setUserLocation(locationData);
        console.log('📍 Location loaded from localStorage:', locationData);
      } catch (err) {
        console.error('❌ Error parsing saved location:', err);
      }
    } else {
      console.log('📍 No location in localStorage');
    }
  }, []);

  // Function to update location - supports both GeoJSON and plain lat/lng
  const updateLocation = (locationData) => {
    if (locationData && locationData.city) {
      const lng =
        locationData.location?.coordinates?.[0] ??
        locationData.lng;
      const lat =
        locationData.location?.coordinates?.[1] ??
        locationData.lat;

      const updatedLocation = {
        lat,
        lng,
        city: locationData.city,
        state: locationData.state,
        timestamp: new Date().toISOString(),
      };

      console.log('📍 Location updated:', updatedLocation);
      setUserLocation(updatedLocation);
      localStorage.setItem('userLocation', JSON.stringify(updatedLocation));
      setError('');
    } else {
      console.error('❌ Invalid location data');
      setError('Invalid location data');
    }
  };

  // Function to clear location
  const clearLocation = () => {
    console.log('🗑️ Location cleared');
    setUserLocation(null);
    localStorage.removeItem('userLocation');
    setError('');
  };

  // Function to get location by GPS
  const getLocationByGPS = async () => {
    setLoading(true);
    setError('');
    console.log('⏳ Getting location from GPS...');

    if (!navigator.geolocation) {
      const errorMsg = 'Geolocation is not supported by your browser';
      setError(errorMsg);
      console.error('❌ Error:', errorMsg);
      setLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude, accuracy } = position.coords;

        console.log('📍 Raw Geolocation Data Received:', {
          latitude,
          longitude,
          accuracy: accuracy + ' meters',
        });

        try {
          // Reverse geocode
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`,
            {
              headers: {
                'Accept-Language': 'en',
              },
            }
          );

          if (!response.ok) throw new Error('Geocoding failed');

          const data = await response.json();
          const address = data.address || {};

          const city =
            address.city ||
            address.town ||
            address.village ||
            address.county ||
            'Unknown City';

          const state =
            address.state ||
            address.province ||
            'Unknown State';

          const locationObject = {
            lat: latitude,
            lng: longitude,
            city,
            state,
            accuracy,
            timestamp: new Date().toISOString(),
          };

          console.log('✅ Location data from GPS:', locationObject);
          updateLocation(locationObject);
        } catch (err) {
          console.error('❌ Reverse geocoding error:', err);
          setError('Failed to determine city from coordinates');
        }

        setLoading(false);
      },
      (error) => {
        setLoading(false);
        let errorMessage = '';

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage =
              'Location permission denied. Enable location in browser settings.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information is unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out.';
            break;
          default:
            errorMessage = 'An error occurred while getting location.';
        }

        console.error('❌ Geolocation Error:', {
          code: error.code,
          message: errorMessage,
        });

        setError(errorMessage);
      },
      {
        // relaxed options to reduce TIMEOUT errors
        enableHighAccuracy: false, // was true
        timeout: 30000,            // was 10000
        maximumAge: 600000,        // was 0
      }
    );
  };

  const locationValue = {
    userLocation,
    loading,
    error,
    updateLocation,
    clearLocation,
    getLocationByGPS,
  };

  return (
    <LocationContext.Provider value={locationValue}>
      {children}
    </LocationContext.Provider>
  );
};

// Custom hook to use location context
export const useLocation = () => {
  const context = useContext(LocationContext);

  if (!context) {
    throw new Error('useLocation must be used within LocationProvider');
  }

  return context;
};

// ============================================================================
// LOCATION DIALOG CONTEXT
// ============================================================================

const LocationDialogContext = createContext();

export const LocationDialogProvider = ({ children }) => {
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [dialogShownOnce, setDialogShownOnce] = useState(false);

  const openLocationDialog = () => {
    console.log('📍 Opening location dialog from context');
    setLocationDialogOpen(true);
  };

  const closeLocationDialog = () => {
    console.log('📍 Closing location dialog from context');
    setLocationDialogOpen(false);
  };

  const openLocationDialogOnce = () => {
    if (!dialogShownOnce) {
      console.log('📍 Opening location dialog for first time');
      setLocationDialogOpen(true);
      setDialogShownOnce(true);
      localStorage.setItem('locationDialogShown', 'true');
    }
  };

  // Load dialog shown status from localStorage on mount
  useEffect(() => {
    const hasShownDialog = localStorage.getItem('locationDialogShown');
    if (hasShownDialog) {
      setDialogShownOnce(true);
      console.log('✅ Dialog already shown before');
    }
  }, []);

  const dialogValue = {
    locationDialogOpen,
    dialogShownOnce,
    openLocationDialog,
    closeLocationDialog,
    openLocationDialogOnce,
  };

  return (
    <LocationDialogContext.Provider value={dialogValue}>
      {children}
    </LocationDialogContext.Provider>
  );
};

// Custom hook to use location dialog context
export const useLocationDialog = () => {
  const context = useContext(LocationDialogContext);

  if (!context) {
    throw new Error('useLocationDialog must be used within LocationDialogProvider');
  }

  return context;
};
